import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {BenchlingAppFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {GetCanvas, GetCanvasVariables} from './__generated/GetCanvas';
import {GetCanvasNonPolling, GetCanvasNonPollingVariables} from './__generated/GetCanvasNonPolling';
import {
  GetResourceFeatureAssociation,
  GetResourceFeatureAssociationVariables,
} from './__generated/GetResourceFeatureAssociation';
import Canvas, {DefaultCanvasPlaceholder} from './BenchlingAppCanvas';

type BenchlingAppCanvasOrInaccessible = GetCanvas['benchlingAppCanvas'];

export const BATCHED_CANVAS_QUERY = gql`
  query GetCanvas($resourceId: StrictString!, $featureType: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingAppCanvas(resourceId: $resourceId, featureType: $featureType) {
      ...BenchlingAppCanvasGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingAppCanvas').fragment}
`;

export const NON_POLLING_CANVAS_QUERY = gql`
  query GetCanvasNonPolling($resourceId: StrictString!, $featureType: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingAppCanvas(resourceId: $resourceId, featureType: $featureType) {
      ...BenchlingAppCanvasGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingAppCanvas').fragment}
`;

export const RESOURCE_FEATURE_ASSOCIATION_QUERY = gql`
  query GetResourceFeatureAssociation($resourceId: StrictString!, $featureType: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    resourceFeatureAssociation(resourceId: $resourceId, featureType: $featureType) {
      ...ResourceFeatureAssociationGraphQL
    }
  }
  ${getFragmentWrapper('ResourceFeatureAssociation').fragment}
`;

interface CanvasContainerProps {
  resourceId: string;
  featureType: BenchlingAppFeatureType;
  enabledOverride: boolean;
}

export default function CanvasContainer(props: CanvasContainerProps): JSX.Element {
  return (
    <BenchlingQuery<GetResourceFeatureAssociation, GetResourceFeatureAssociationVariables>
      query={RESOURCE_FEATURE_ASSOCIATION_QUERY}
      renderLoading={() => <></>}
      variables={{resourceId: props.resourceId, featureType: props.featureType}}
    >
      {(data, {refetch}) => {
        if (isInaccessible(data.resourceFeatureAssociation)) {
          return <></>;
        }
        const appName = data.resourceFeatureAssociation.appSummary.name;
        return <CanvasRenderer {...props} appName={appName} />;
      }}
    </BenchlingQuery>
  );
}

interface CanvasRendererProps {
  resourceId: string;
  appName: string;
  featureType: BenchlingAppFeatureType;
  enabledOverride: boolean;
}

export function CanvasRenderer(props: CanvasRendererProps): JSX.Element {
  const [canvas, updateCanvas] = useState<BenchlingAppCanvasOrInaccessible>();
  const onQueryComplete = (data: GetCanvas): void => {
    updateCanvas(data.benchlingAppCanvas);
  };
  const queryVariables = {resourceId: props.resourceId, featureType: props.featureType};

  // Kick off background continuous polling query. These queries are batched by Apollo
  // (see createBatchLink.ts), so that if there are many canvases on the page, we don't overload the network
  // with many GQL polling loops hitting the backend.
  const batchedPollingQuery = useBenchlingQuery<GetCanvas, GetCanvasVariables>(BATCHED_CANVAS_QUERY, {
    variables: queryVariables,
    pollInterval: BenchlingConfig.BENCHLING_APP_CANVAS_POLLING_FREQUENCY_MS,
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
    onCompleted: onQueryComplete,
  });

  // Kick off immediate, non-batched, non-polling query on initial pageload. Use this query when you want to
  // get an immediate result (e.g. when refetching canvas data in response to a user action) rather than wait
  // for the polling interval / batch to execute.
  const nonPollingQuery = useBenchlingQuery<GetCanvasNonPolling, GetCanvasNonPollingVariables>(
    NON_POLLING_CANVAS_QUERY,
    {
      variables: queryVariables,
      renderCachedDataWhenRefetching: true,
      onCompleted: onQueryComplete,
    }
  );

  // Once a canvas has been received, keep rendering it while queries are in flight: unmounting the canvas
  // would discard the user's unsaved input every polling interval.
  const isLoadingInitialCanvas =
    canvas === undefined &&
    (nonPollingQuery.type === QueryResultType.PENDING ||
      batchedPollingQuery.type === QueryResultType.PENDING);
  if (
    isLoadingInitialCanvas ||
    nonPollingQuery.type === QueryResultType.ERROR ||
    batchedPollingQuery.type === QueryResultType.ERROR
  ) {
    return <DefaultCanvasPlaceholder benchlingAppName={props.appName} />;
  }

  return (
    <div className="canvasContainer">
      {canvas && !isInaccessible(canvas) ? (
        <div className={css(styles.canvasWrapper)}>
          <Canvas
            canvas={canvas}
            refetchCanvases={async () => {
              await nonPollingQuery.refetch();
            }}
            enabledOverride={props.enabledOverride}
            dataTestAttrs={{component: 'Canvas', element: 'root'}}
          />
        </div>
      ) : (
        <DefaultCanvasPlaceholder benchlingAppName={props.appName} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  canvasWrapper: {
    marginTop: '30px',
  },
});
